import React, { Fragment, useState } from 'react'
import styled from '@emotion/styled'
import { Form, Icon, Tabs, Steps } from 'antd'
import { apiClient } from 'utils/api-client'
import { getAuthHeader } from 'utils/auth'
import { ContentContainer, StyledAlert, PageAlert } from 'components'
import BasicInfo from 'views/ScanSettings/NewScan/steps/BasicInfo'
import PreFlowStep from 'views/ScanSettings/NewScan/steps/PreFlowStep'
import ScanStep from 'views/ScanSettings/NewScan/steps/ScanStep'
import PostFlowStep from 'views/ScanSettings/NewScan/steps/PostFlowStep'
import steps, { createNewScanPayload } from 'views/ScanSettings/NewScan/steps/steps'
import ValidationErrors from 'views/ScanSettings/NewScan/components/ValidationErrors'
import Help from 'views/ScanSettings/NewScan/components/Help'
import {
	StyledNewScan,
	NewScanHeader,
	NewScanSteps,
	HeaderTitle,
	NewScanContent,
	NewScanFooter,
	MainActionButton,
	StyledNewScanContainer
} from 'views/ScanSettings/NewScan/style'
import {Link} from '@reach/router'



const { Step } = Steps
const { TabPane } = Tabs

const NewScanTop = styled.div({
	display: 'grid',
	gridTemplateColumns: '1fr 162px',
	alignItems: 'center',
	padding: '0 10px 20px 10px',
	h1: {
		margin: 0,
		padding: 0
	}
})

const StepDescription = styled.span({
	fontSize: '0.5rem',
	fontWeight: 600
})

const NewScanForm = ({
	project,
	form,
	restrictions,
	loadingRestrictions,
	cookies,
	loadingCookies,
	labels,
	loadingLabels,
	flows,
	loadingFlows,
	webhooks,
	loadingWebhooks,
	filters,
	rules,
	error,
	executeNewScanSettings,
	newScanError
}) => {
	const [validationError, setValidationError] = useState(null)
	const [isValidating, setIsValidating] = useState(false)
	const [currentStep, setCurrentStep] = useState(0)
	const { getFieldsValue } = form

	const validateStep = async () => {
		setValidationError(null)
		setIsValidating(true)
		const errors = await steps[currentStep].validate(form, project.name, restrictions)
		setIsValidating(false)
		if (!errors) {
			return Promise.resolve(true)
		} else {
			if (errors && errors.origin === 'server') {
				setValidationError(errors.errors)
			}
			return Promise.reject(errors.errors)
		}
	}
	const nextStep = async () => {
		try {
			steps[currentStep].validate && await validateStep()
			setValidationError(null);
			const nextStep = currentStep + 1
			setCurrentStep(nextStep)
		} catch (e) {
			// do not move to the next step
		}
	}

	const prevStep = () => setCurrentStep(currentStep - 1)

	const runScan = async () => {
		try {
			steps[currentStep].validate && await validateStep()
			const allValues = getFieldsValue()
			const newScanPayload = await createNewScanPayload(allValues, restrictions)
			newScanPayload.errors && setValidationError(newScanPayload.errors)
			!newScanPayload.errors && executeNewScanSettings({ data: newScanPayload })
		} catch (e) {
			setValidationError(e)
		}
	}
	
	let scanErrors = newScanError && newScanError.data && newScanError.data.Errors;

	return (
		<Fragment>
			{error && <PageAlert message={error.data.message || error.data.Errors} />}
			<StyledNewScanContainer>
			<StyledNewScan>
				<NewScanHeader>
					<NewScanTop>
						<HeaderTitle>NEW SCAN SETTINGS</HeaderTitle>
						<Link to="../new-simple-scan">
							<strong>SWITCH TO SIMPLE MODE</strong>
						</Link>
					</NewScanTop>
					<NewScanSteps>
						<Steps current={currentStep}>
							<Step title={steps[0].title} />
							<Step title={steps[1].title} description={<StepDescription>**OPTIONAL**</StepDescription>} />
							<Step title={steps[2].title} />
							<Step title={steps[3].title} description={<StepDescription>**OPTIONAL**</StepDescription>} />
						</Steps>
					</NewScanSteps>
				</NewScanHeader>
				<NewScanContent>
					{(validationError || newScanError) && (
						<StyledAlert
							style={{ marginBottom: 20 }}
							message="Error"
							description={<ValidationErrors errors={validationError || scanErrors} />}
							type="error"
							showIcon
						/>
					)}
					<Form layout="vertical">
						<Tabs defaultActiveKey={steps[0].name} activeKey={steps[currentStep].name}>
							<TabPane tab={null} key={steps[0].name}>
								<BasicInfo form={form} />
							</TabPane>
							<TabPane tab={null} key={steps[1].name}>
								<PreFlowStep
									form={form}
									cookies={cookies}
									loadingCookies={loadingCookies}
									labels={labels}
									loadingLabels={loadingLabels}
									flows={flows}
									loadingFlows={loadingFlows}
									webhooks={webhooks}
									loadingWebhooks={loadingWebhooks}
									setValidationError={setValidationError}
								/>
							</TabPane>
							<TabPane tab={null} key={steps[2].name}>
								<ScanStep
									form={form}
									restrictions={restrictions}
									flows={flows}
									loadingRestrictions={loadingRestrictions}
									simpleMode={false}
									setValidationError={setValidationError}
								/>
							</TabPane>
							<TabPane tab={null} key={steps[3].name}>
								<PostFlowStep
									form={form}
									filters={filters}
									webhooks={webhooks}
									rules={rules}
									setValidationError={setValidationError}
									setIsValidating={setIsValidating}
								/>
							</TabPane>
						</Tabs>
					</Form>
				</NewScanContent>
				<NewScanFooter>
					{currentStep > 0 && (
						<MainActionButton size="large" icon="arrow-left" onClick={prevStep}>
							BACK
						</MainActionButton>
					)}
					{currentStep < steps.length - 1 && (
						<MainActionButton type="primary" id="nextStep" size="large" onClick={nextStep} loading={isValidating}>
							NEXT
							<Icon type="arrow-right" />
						</MainActionButton>
					)}
					{currentStep === steps.length - 1 && (
						<MainActionButton size="large"  id="saveScan" type="primary" onClick={runScan}>
							SAVE SCAN
							<Icon type="arrow-right" />
						</MainActionButton>
					)}
				</NewScanFooter>
			</StyledNewScan>
				<ContentContainer>
					<Help form={form} currentStep={steps[currentStep].name} />
				</ContentContainer>
			</StyledNewScanContainer>
		</Fragment>
	)
}

export default Form.create({})(NewScanForm)
